import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ConfigProvider, theme as antTheme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import Layout from './components/Layout';
import Home from './pages/Home';
import DataTemplate from './pages/DataTemplate';
import TemplatePreview from './pages/TemplatePreview';
import SmartChartUpload from './pages/SmartChartUpload';
import SmartChartDataSelect from './pages/SmartChartDataSelect';
import SmartChartTypeSelect from './pages/SmartChartTypeSelect';
import SmartChartResult from './pages/SmartChartResult';
import SmartChartWorkspace from './pages/SmartChartWorkspace';
import AnalysisWorkspace from './pages/AnalysisWorkspace';
import AnalysisUpload from './pages/AnalysisUpload';
import AnalysisDataSelect from './pages/AnalysisDataSelect';
import AnalysisTypeSelect from './pages/AnalysisTypeSelect';
import AnalysisResult from './pages/AnalysisResult';

// Error Boundary to catch React crashes and show error instead of white screen
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[trendata] React crash:', error, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, maxWidth: 800, margin: '0 auto', fontFamily: 'monospace' }}>
          <h2 style={{ color: '#f5222d' }}>页面渲染出错</h2>
          <pre style={{ background: '#fff1f0', padding: 16, borderRadius: 8, overflow: 'auto', fontSize: 12, border: '1px solid #ffa39e' }}>
            {this.state.error?.message}
            {'\n\n'}
            {this.state.error?.stack}
          </pre>
          <button onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            style={{ marginTop: 16, padding: '8px 24px', background: '#533afd', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
            刷新页面
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const AppInner: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: isDark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#533afd',
          borderRadius: 6,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        },
      }}
    >
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="templates" element={<DataTemplate />} />
            <Route path="templates/preview" element={<TemplatePreview />} />
            <Route path="smart-chart/workspace" element={<SmartChartWorkspace />} />
            <Route path="smart-chart/upload" element={<SmartChartUpload />} />
            <Route path="smart-chart/data-select" element={<SmartChartDataSelect />} />
            <Route path="smart-chart/type-select" element={<SmartChartTypeSelect />} />
            <Route path="smart-chart/result" element={<SmartChartResult />} />
            <Route path="analysis/workspace" element={<ErrorBoundary><AnalysisWorkspace /></ErrorBoundary>} />
            <Route path="analysis/upload" element={<AnalysisUpload />} />
            <Route path="analysis/data-select" element={<AnalysisDataSelect />} />
            <Route path="analysis/type-select" element={<AnalysisTypeSelect />} />
            <Route path="analysis/result" element={<AnalysisResult />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <AppInner />
  </ThemeProvider>
);

export default App;
